/**
 * Environment Store
 *
 * Manages runtime prod/dev environment switching for the Stream app, plus
 * runtime operator switching. Persists the active environment to
 * localStorage so it survives restarts.
 *
 * Architecture:
 * - Both environment configs (API keys + gateway URLs) are pre-fetched at
 *   startup for the selected operator.
 * - On environment switch: API client is reconfigured (which aborts all
 *   active SSE streams), then `switchGeneration` is incremented to force
 *   a React remount of the RouterProvider.
 * - On operator switch: fresh configs are fetched for the new operator's
 *   consumer_id (Rust caches per-consumer), the API client is reconfigured,
 *   and `switchGeneration` is incremented. The remount naturally re-fires
 *   all useEffect hooks, re-establishing SSE subscriptions and re-fetching
 *   data against the new identity.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  getEnvironmentConfigs,
  type EnvironmentConfig,
  type EnvironmentConfigs,
} from '@/lib/tauri'
import { api } from '@/lib/api'

export type EnvironmentName = 'prod' | 'dev'

interface EnvironmentState {
  /** Currently active environment. */
  active: EnvironmentName
  /** Both environment configurations (null before initialization). */
  configs: EnvironmentConfigs | null
  /** Whether the store has been initialized with configs. */
  isInitialized: boolean
  /** Incremented on each switch — used as React key to force remount. */
  switchGeneration: number

  /**
   * Initialize with pre-fetched configs from the Rust layer. Configures the
   * API client for the persisted active env. The caller fetches the configs
   * (typically in the bootstrap) and hands them in here.
   */
  initialize: (configs: EnvironmentConfigs) => void

  /** Switch to a different environment. Reconfigures API client and increments switchGeneration. */
  switchEnvironment: (env: EnvironmentName) => void

  /**
   * Fetch fresh environment configs for a different operator and apply them.
   *
   * Aborts all active SSE streams via `api.reconfigure()` and increments
   * `switchGeneration` to trigger a React remount. Rust caches per-consumer
   * so re-selection of a previously seen operator is fast.
   *
   * The currently active env is preserved if still available; otherwise
   * falls back to the other env (mirrors `initialize` behaviour).
   */
  reloadForOperator: (consumerId: string) => Promise<void>

  /** Get the config for the currently active environment. */
  getActiveConfig: () => EnvironmentConfig | null
}

export const useEnvironmentStore = create<EnvironmentState>()(
  persist(
    (set, get) => ({
      active: 'prod',
      configs: null,
      isInitialized: false,
      switchGeneration: 0,

      initialize: (configs) => {
        const { active } = get()

        // If the persisted active env is unavailable, fall back to the other one
        let resolvedActive = active
        const activeConfig = configs[resolvedActive]
        if (activeConfig.unavailable) {
          resolvedActive = resolvedActive === 'prod' ? 'dev' : 'prod'
          const fallbackConfig = configs[resolvedActive]
          if (fallbackConfig.unavailable) {
            // Both unavailable — this shouldn't happen (Rust layer checks this)
            // but handle defensively
            console.error('[EnvironmentStore] Both environments unavailable')
            set({ configs, isInitialized: true, active: resolvedActive })
            return
          }
        }

        const config = configs[resolvedActive]
        api.configure(config.api_key, config.gateway_url)
        set({ configs, isInitialized: true, active: resolvedActive })
      },

      switchEnvironment: (env) => {
        const { configs, active, switchGeneration } = get()
        if (!configs || env === active) return

        const config = configs[env]
        if (config.unavailable) {
          console.warn(`[EnvironmentStore] Cannot switch to ${env}: unavailable`)
          return
        }

        // reconfigure() aborts all active SSE streams automatically
        api.reconfigure(config.api_key, config.gateway_url)
        set({ active: env, switchGeneration: switchGeneration + 1 })
      },

      reloadForOperator: async (consumerId) => {
        const trimmed = consumerId.trim()
        if (!trimmed) {
          throw new Error(
            '[EnvironmentStore] reloadForOperator called with empty consumer_id',
          )
        }

        // Rust caches per-consumer so re-selection of a previously seen
        // operator returns instantly.
        const configs = await getEnvironmentConfigs(trimmed)

        // Resolve the active environment: prefer current `active` if it is
        // still available, otherwise fall back to the other env. Mirrors
        // `initialize()`.
        const { active, switchGeneration } = get()
        let resolvedActive: EnvironmentName = active
        if (configs[resolvedActive].unavailable) {
          const fallback: EnvironmentName =
            resolvedActive === 'prod' ? 'dev' : 'prod'
          if (configs[fallback].unavailable) {
            // Both unavailable — surface to the caller so the splash can
            // render an error state.
            set({ configs })
            throw new Error(
              `Neither environment is available for operator '${trimmed}'.`,
            )
          }
          resolvedActive = fallback
        }

        const config = configs[resolvedActive]
        // reconfigure() aborts all active SSE streams from the previous
        // operator's session before swapping the API key + gateway URL.
        api.reconfigure(config.api_key, config.gateway_url)

        set({
          configs,
          active: resolvedActive,
          isInitialized: true,
          switchGeneration: switchGeneration + 1,
        })
      },

      getActiveConfig: () => {
        const { configs, active } = get()
        return configs ? configs[active] : null
      },
    }),
    {
      name: 'stream-environment',
      partialize: (s) => ({ active: s.active }),
    },
  ),
)
